/**
 * TRIM round (freeform spike Phase 1 — docs/bim-plan/13 §3): the curated trim-profile
 * vocabulary. Profiles are PROCEDURAL, generated from a small closed set of kinds +
 * two dimensions — never free-drawn per element (types doctrine: curated firm
 * standards; the drawable profile editor is a recorded later round). The kinds here
 * are deliberately few and honest — enforcement by omission, the gutter-profile rule.
 *
 * Coordinate convention: a profile is a closed ring in (o, z) —
 *   o = horizontal projection OFF the drawn path, toward the path's LEFT normal
 *       (an instance `flip` mirrors it to the other side),
 *   z = vertical, 0 at the run's base elevation (level datum + heightOffsetFt;
 *       an instance `flipV` mirrors the profile downward for under-side coves —
 *       the flared balcony/soffit read).
 * o = 0 is the BACK of the trim (the plane of the drawn path — typically a wall
 * face); the profile projects outward from it. Units here are FEET (callers pass
 * the type's inch params / 12).
 */
import type { Pt2 } from './types'

export const TRIM_PROFILE_KINDS = ['board', 'eased', 'cove', 'bullnose', 'crown'] as const
export type TrimProfileKind = (typeof TRIM_PROFILE_KINDS)[number]

export const TRIM_PROFILE_LABELS: Record<TrimProfileKind, string> = {
  board: 'Board (S4S)',
  eased: 'Eased Board',
  cove: 'Cove',
  bullnose: 'Bullnose',
  crown: 'Crown (Coved)',
}

const ARC_SEGS = 8

/** quarter-ellipse arc from angle a0 to a1 (radians) about (cx, cz), radii (ro, rz) */
function arc(cx: number, cz: number, ro: number, rz: number, a0: number, a1: number, segs = ARC_SEGS): Pt2[] {
  const out: Pt2[] = []
  for (let k = 0; k <= segs; k++) {
    const t = a0 + ((a1 - a0) * k) / segs
    out.push([cx + ro * Math.cos(t), cz + rz * Math.sin(t)])
  }
  return out
}

/** drop consecutive duplicate points (arc endpoints meeting fillet corners) */
function dedupe(ring: Pt2[]): Pt2[] {
  const out: Pt2[] = []
  for (const p of ring) {
    const last = out[out.length - 1]
    if (!last || Math.abs(last[0] - p[0]) > 1e-9 || Math.abs(last[1] - p[1]) > 1e-9) out.push(p)
  }
  while (out.length > 1) {
    const a = out[0], b = out[out.length - 1]
    if (Math.abs(a[0] - b[0]) > 1e-9 || Math.abs(a[1] - b[1]) > 1e-9) break
    out.pop()
  }
  return out
}

/**
 * The (o, z) ring for a trim profile kind at height H × projection P (both FEET).
 * Rings are CCW-or-any-winding — the sweep normalizes. Every kind starts at the
 * back-bottom [0, 0] and closes up the back face, so a run always seats flush
 * against the plane it was drawn on.
 */
export function trimProfileRing(kind: TrimProfileKind, heightFt: number, projFt: number): Pt2[] {
  const H = Math.max(heightFt, 1 / 96)
  const P = Math.max(projFt, 1 / 96)
  switch (kind) {
    case 'board':
      return [[0, 0], [P, 0], [P, H], [0, H]]
    case 'eased': {
      // flat board, outer TOP corner eased with a quarter round (r = the lesser of
      // half the projection or a 3/4" ease — reads as stock eased trim, not a shape)
      const r = Math.min(P / 2, H / 3, 0.0625)
      return dedupe([
        [0, 0], [P, 0], [P, H - r],
        ...arc(P - r, H - r, r, r, 0, Math.PI / 2),
        [0, H],
      ])
    }
    case 'cove': {
      // concave quarter face springing from a small bottom fillet to the top back —
      // the scotia/flared-band read (flipV turns it into the under-side flare).
      // Quarter ellipse about (P, H): from (P, f) at θ=3π/2 up-and-back to (f, H) at
      // θ=π — points bow toward the back-bottom, i.e. concave from outside.
      const f = Math.min(P, H) * 0.18
      return dedupe([
        [0, 0], [P, 0], [P, f],
        ...arc(P, H, P - f, H - f, (3 * Math.PI) / 2, Math.PI),
        [0, H],
      ])
    }
    case 'bullnose': {
      // half-round(ish) face: back plate with a half-ellipse belly to projection P
      return dedupe([
        [0, 0],
        ...arc(0, H / 2, P, H / 2, -Math.PI / 2, Math.PI / 2),
        [0, H],
      ])
    }
    case 'crown': {
      // coved crown: square fillets top and bottom with the concave sweep between —
      // the projection lands at the TOP (crown projects at its top edge)
      const f = Math.min(P, H) * 0.15
      return dedupe([
        [0, 0], [f * 1.6, 0], [f * 1.6, f],
        // concave from (1.6f, f) out-and-up to (P, H − f): quarter ellipse about the
        // OUTER corner (P, f), θ from π to π/2 — bows toward the back, concave
        ...arc(P, f, P - f * 1.6, H - 2 * f, Math.PI, Math.PI / 2),
        [P, H], [0, H],
      ])
    }
  }
}

/**
 * WALL TERMINATION arcs (freeform §8.12 — Matt: "arcs at the bottom and the top,
 * both concave and convex"; DIRECTION FIX 2026-08-20, Matt's screenshot: "the arch
 * is supposed to face perpendicular to the wall for its length" — the arc reads in
 * the wall's FACE, spanning the run's LENGTH, not across its thickness; the first
 * build swept a bullnose section across the thickness, which was wrong and is gone).
 *
 * `wallEndArcZFt` is the arc's ordinate: a CIRCULAR SEGMENT over chord L with rise
 * h — 0 at both ends, h at mid-span, tangent-angled ends (the shallow-eyebrow read
 * matching Matt's sketch; a half-ellipse's vertical end tangents read wrong for
 * shallow arches). The wall generator composes the four cases (top/bottom ×
 * convex/concave) from this one ordinate as per-point z surfaces on a varying-z
 * prism band over the wall's own plan footprint.
 */
export type WallEndProfile = 'flat' | 'archConvex' | 'archConcave'
export const WALL_END_PROFILES: WallEndProfile[] = ['flat', 'archConvex', 'archConcave']

export function wallEndArcZFt(riseFt: number, s: number, lengthFt: number): number {
  const L = Math.max(lengthFt, 1 / 96)
  const h = Math.max(Math.min(riseFt, L / 2, 4), 1 / 96)
  const x = Math.min(Math.max(s, 0), L) - L / 2
  const R = (L * L) / (8 * h) + h / 2
  return Math.max(0, Math.sqrt(Math.max(0, R * R - x * x)) - (R - h))
}

/** the effective (clamped) rise the arc actually uses — callers size the cap band by it */
export function wallEndArcRiseFt(riseFt: number, lengthFt: number): number {
  const L = Math.max(lengthFt, 1 / 96)
  return Math.max(Math.min(riseFt, L / 2, 4), 1 / 96)
}
